import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import {
  Component,
  computed,
  inject,
  input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy, output
} from "@angular/core";
import { MatCardTitle } from "@angular/material/card";
import { MatIcon } from "@angular/material/icon";
import { MatTableModule } from "@angular/material/table";
import { MatMenuModule } from "@angular/material/menu";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import dayjs from "dayjs";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { LorenDateModule } from "../../../../shared/pipes/loren-date/loren-date.module";
import { LorenPageContentComponent } from "../../../../shared/modules/loren-page-content/loren-page-content.component";
import { LorenFiltersComponent } from "../../../../shared/modules/loren-filters/loren-filters.component";
import { LorenAsset } from "../../../loren-assets/interfaces/loren-asset.interface";
import {
  LorenMeteringType,
  LorenMeteringTypeTranslation,
} from "../../../loren-assets/interfaces/loren-metering-type";
import { LorenAlarmsService } from "../../../../shared/services/loren-alarms-service/loren-alarms.service";
import {
  LorenHomeAlarmsTableActionsComponent
} from "./components/loren-home-alarms-table-actions/loren-home-alarms-table-actions.component";
import {
  LorenAlarmsFilter
} from "./constants/loren-alarms-filters.constant";
import { LorenAlarmsColumns } from "./constants/loren-alarms.columns.constants";
import { LorenAlarmsSorting } from "./constants/loren-alarms-sorting.constant";
import { LorenMetricBinaryValues } from "../../../../shared/modules/loren-asset-card/constants/loren-metric-icons";
import {
  LorenMetricCategories,
  LorenMetricKinds,
} from "../../../../shared/modules/loren-asset-card/constants/loren-metric-types.constants";
import { LorenMetricUnits } from "../../../../shared/modules/loren-asset-card/constants/loren-metric-units.constants";
import { LorenBaseFeature } from "../../../../shared/base-components/loren-base-feature/loren-base-feature";
import { AssetGroupService } from "../../../asset/asset-group.service";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { unique } from "remeda";
import { formatNumericValue } from "../../../asset/metric.utils";
import {
  getFillLevelTranslationKey,
  isFillLevelSensor,
} from "../../../asset/fill-level.utils";
import { MatButton } from "@angular/material/button";
import { map, startWith } from "rxjs/operators";
import { combineLatest, switchMap } from "rxjs";
import {
  AlarmFilters
} from "../../../../shared/services/loren-alarms-service/services/loren-alarms-endpoints-service/loren-alarms-endpoints.service";
import { BadgeComponent } from "../../../../common/badge/badge.component";
import {
  LorenAutomationPlanService
} from "../../../loren-automation-plans/services/loren-automation-plan-service/loren-automation-plan.service";
import {
  LorenAuthenticationService
} from "../../../../shared/services/loren-authentication-service/loren-authentication.service";

@Component({
  imports: [
    CommonModule,
    FormsModule,
    MatIcon,
    MatTableModule,
    MatMenuModule,
    MatProgressSpinner,
    LorenDateModule,
    LorenPageContentComponent,
    LorenFiltersComponent,
    TranslocoPipe,
    LorenHomeAlarmsTableActionsComponent,
    MatButton,
    MatCardTitle,
    BadgeComponent,
  ],
  selector: "loren-home-alarms-table",
  styleUrls: ["./loren-home-alarms-table.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: "./loren-home-alarms-table.component.html",
})
export class LorenHomeAlarmsTableComponent
  extends LorenBaseFeature
  implements OnInit, OnChanges {
  private transloco = inject(TranslocoService);
  private alarmsService = inject(LorenAlarmsService);
  private readonly assetGroupService = inject(AssetGroupService);
  private readonly automationPlanService = inject(LorenAutomationPlanService);
  private readonly authenticationService = inject(LorenAuthenticationService);

  assets = input<LorenAsset[]>();
  inputFilter = input<"critical" | "warning">(null);
  filtersCleared = output<void>();
  @ViewChild("alarmsTable") alarmsTable;

  readonly alarmColumns = LorenAlarmsColumns;
  readonly columns = [
    "rowNum",
    this.alarmColumns.STATUS,
    this.alarmColumns.AUTOMATION_RULE,
    this.alarmColumns.ASSET,
    this.alarmColumns.GROUPS,
    this.alarmColumns.PARAMETER,
    this.alarmColumns.START,
  ];
  assetsFilter: { key: string; value: number }[];
  parametersFilter: { key: string; value: string }[];
  loading = signal(false);
  activeFilters = signal<AlarmFilters | undefined>(undefined);

  activeFilters$ = toObservable(this.activeFilters);
  private readonly automationRuleNamesById$ = this.automationPlanService
    .getAllPlans(this.authenticationService.userId)
    .pipe(
      map(res => new Map(res.items.map(p => [p.id, p.name]))),
      startWith(new Map<number, string>()),
    );
  alarms$ = combineLatest([this.activeFilters$, this.automationRuleNamesById$]).pipe(
    switchMap(([filters, ruleNamesById]) => {
      return this.alarmsService.getActiveAlarms(0, 100, filters ?? {})
        .pipe(
          map(res => {
            this.totalNumOfAlarms.set(res.total);
            return res.data.map((i) => ({
              asset: Object.values(i.assets).join(", "),
              groups: Object.values(i.groups).join(", "),
              parameter: i.meteringName,
              value: this.getValue(i.meteringType, i.meteringName, i.meteringValue),
              automationRule: ruleNamesById.get(i.automationRuleId) ?? "",
              start: new Date(i.startedAt),
              isCritical: i.isCritical,
            }))
          })
        )
    })
  );
  alarms = toSignal(this.alarms$);
  totalNumOfAlarms = signal(undefined);
  sorted = signal({});
  alarmsError = signal(false);
  alarmsSortedFiltered = computed(() => {
    const alarms = this.alarms() ?? [];
    const sortConfig = this.sorted();
    const [column, direction] = Object.entries(sortConfig)[0] ?? [];

    if (!column || !direction) {
      return alarms;
    }

    const multiplier = direction === LorenAlarmsSorting.ASCENDING ? 1 : -1;
    const sorted = [...alarms];

      sorted.sort((a, b) => {
          const valA = a[column];
          const valB = b[column];

          if (valA instanceof Date && valB instanceof Date) {
              return (valA.getTime() - valB.getTime()) * multiplier;
          }

          if (typeof valA === "string" && typeof valB === "string") {
              return valA.localeCompare(valB) * multiplier;
          }

          if (typeof valA === "number" && typeof valB === "number") {
              return (valA - valB) * multiplier;
          }

          return 0;
      });

    return sorted;
  })
  hasAlarmsToShow = computed(
    () =>
      this.alarms()?.length ||
      Object.values(this.activeFilters() ?? {}).filter((v) => v).length
  );
  readonly alarmFilters = LorenAlarmsFilter;

  private readonly group$ = this.assetGroupService.all;
  private readonly groups = toSignal(this.group$);
  protected readonly groupsFilter = computed(() => {
    const groups = this.groups();
    const assetGroups = unique(this.assets().flatMap(x => x.belongsToGroups));


    return groups.filter(x => assetGroups.includes(x.id)).map((g) => ({
      key: g.name,
      value: g.name,
    }))
  });

  readonly startTimeFilters = [
    {
      key: this.transloco.translate("alarms.last_day"),
      value: this.prepareDateForFilters(24, "hour"),
    },
    {
      key: this.transloco.translate("alarms.last_week"),
      value: this.prepareDateForFilters(7, "day"),
    },
    {
      key: this.transloco.translate("alarms.last_month"),
      value: this.prepareDateForFilters(30, "day"),
    },
    {
      key: this.transloco.translate("alarms.last_half_year"),
      value: this.prepareDateForFilters(6, "month"),
    },
  ];

  ngOnInit() {
    this.assetsFilter = this.assets().map((a) => ({
      key: a.name,
      value: a.id,
    }));
    this.parametersFilter = this.getParametersFilters();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.inputFilter) {
      if (changes.inputFilter.firstChange) return;
      this.activeFilters.update((v) => {
          const newValue = {...v};
          const inputFilter = this.inputFilter();

          if(inputFilter === 'critical') {
              newValue[this.alarmFilters.IS_CRITICAL] = 'true';
          } else if (inputFilter === 'warning') {
              newValue[this.alarmFilters.IS_CRITICAL] = 'false';
          } else {
              delete newValue[this.alarmFilters.IS_CRITICAL];
          }

          return newValue;
      });
    }
  }

  alarmsIncreasedHandler() {
    this.sorted.set({});
  }

  alarmsDecreasedHandler() {
    this.sorted.set({});
  }

  selectionChangedHandler() {
    this.sorted.set({});
  }

  filter(filter, $event) {
    const filterValue = Array.from($event.keys())
      .filter((k) => $event.get(k))
      .join(",");
    this.activeFilters.update((v) => ({ ...v, [filter]: filterValue }));
    this.sorted.set({});
  }

  clearAllFilters() {
    this.activeFilters.set(undefined);
    this.sorted.set({});
    this.filtersCleared.emit();
  }

  sort(column: string) {
    switch (this.sorted()[column]) {
      case undefined:
        this.sorted.set({ [column]: LorenAlarmsSorting.ASCENDING });
        break;
      case LorenAlarmsSorting.ASCENDING:
        this.sorted.set({ [column]: LorenAlarmsSorting.DESCENDING });
        break;
      case LorenAlarmsSorting.DESCENDING:
        this.sorted.set({});
        break;
    }
  }

  private getValue(type: LorenMeteringType, name: string, value) {
    switch (LorenMetricKinds.get(type)) {
      case LorenMetricCategories.BINARY:
        return this.transloco.translate(
          LorenMetricBinaryValues.get(type)[value]
        );
      case LorenMetricCategories.NUMERIC:
        if (isFillLevelSensor(type)) {
          const translationKey = getFillLevelTranslationKey(value);
          return translationKey ? this.transloco.translate(translationKey) : value;
        }

        return formatNumericValue(value) + LorenMetricUnits.get(type);
      case LorenMetricCategories.PERCENTAGE:
        if (!value) {
          return '';
        }

        return (Number(value) * 100).toFixed(2) + (LorenMetricUnits.get(type) ?? '');
      case LorenMetricCategories.TRACKING:
        if (name === 'LOW BATTERY') { // Hard-coded value in backend
          return Number(value);
        }

        return [
          value?.find((d) => d.key === "lat")?.value,
          value?.find((d) => d.key === "lng")?.value,
        ].join(", ");
      default:
        return value;
    }
  }

  private getParametersFilters() {
    const parametersSet = new Set([]);
    this.assets().forEach((a) => {
      a.metering.forEach((m) => parametersSet.add(m.type));
    });
    return Array.from(parametersSet).map((p) => ({
      key: this.transloco.translate(LorenMeteringTypeTranslation.get(p)),
      value: p,
    }));
  }

  private prepareDateForFilters(value: number, unit: "hour" | "day" | "month") {
    return `${dayjs().subtract(value, unit).format("YYYY-MM-DD")}T00:00:00`;
  }
}
